import { html, paragraphs } from '../lib/html.js';
import { icon } from '../lib/icons.js';
import { getProgress } from '../lib/store.js';
import { getIndex, chapter, chapterStats, chapterSky, lessonMinutes } from '../lib/content.js';
import { roman, plural } from '../lib/format.js';
import { topbar, avatar, SPEAKERS } from '../components/chrome.js';
import notfound from './notfound.js';

function lessonRow(l, progress, nextId) {
  const done = !!progress.lessons[l.id];
  const current = progress.current && progress.current.lesson === l.id && !done;
  const isNext = l.id === nextId;
  const verses = l.ch === 0 ? `${l.cards.length} short cards` : l.from === l.to ? `Verse ${l.from}` : `Verses ${l.from} to ${l.to}`;
  return html`<li>
    <a class="lesson-row${done ? ' is-done' : ''}${isNext ? ' is-next' : ''}" href="/lesson/${l.id}">
      <span class="lesson-row__mark" aria-hidden="true">${done ? icon('check', { size: 18, stroke: 2.5 }) : l.n}</span>
      <span class="lesson-row__body">
        <span class="lesson-row__title">${l.title}</span>
        <span class="lesson-row__idea">${l.idea}</span>
        <span class="lesson-row__meta">${verses} · ${lessonMinutes(l)} min${done ? ' · Completed' : current ? ' · In progress' : ''}</span>
      </span>
      <span class="lesson-row__go" aria-hidden="true">${isNext ? html`<span class="badge badge--accent">${current ? 'Resume' : 'Next'}</span>` : icon('chevron-right', { size: 18 })}</span>
    </a>
  </li>`;
}

export default async function chapterView({ ch: raw }) {
  const ch = Number(raw);
  const idx = getIndex();
  const isPrologue = raw === '0';
  if (!isPrologue && !(ch >= 1 && ch <= 18 && String(ch) === raw)) return notfound();
  const meta = isPrologue ? idx.prologue : chapter(ch);
  const progress = getProgress();
  const s = chapterStats(progress, ch);
  const sky = chapterSky(ch);
  const part = isPrologue ? null : idx.parts.find((p) => p.chapters.includes(ch));
  const nextLesson = meta.lessons.find((l) => !progress.lessons[l.id]);
  const resuming = nextLesson && progress.current && progress.current.lesson === nextLesson.id;
  const cta = s.complete
    ? { label: isPrologue ? 'Read it again' : 'Read the chapter again', href: isPrologue ? '/lesson/0-1' : `/read/${ch}` }
    : { label: s.done === 0 && !resuming ? (isPrologue ? 'Begin' : 'Start chapter') : `Continue: lesson ${nextLesson.n}`, href: `/lesson/${nextLesson.id}` };
  const famous = isPrologue ? [] : idx.famous.filter((f) => f.ch === ch);
  const prev = ch > 1 ? chapter(ch - 1) : null;
  const next = ch < 18 ? chapter(ch + 1) : null;

  return {
    title: isPrologue ? 'Background' : `Chapter ${ch}: ${meta.title}`,
    tint: sky.top,
    html: html`<div class="chapter" style="--sky-top:${sky.top};--sky-bottom:${sky.bottom};--sky-ink:${sky.ink}">
      ${topbar({ clear: true, back: '/journey', title: isPrologue ? 'Background' : `Chapter ${ch}` })}
      <section class="chapter-hero">
        <div class="wrap chapter-hero__inner">
          <span class="chapter-hero__num" aria-hidden="true">${isPrologue ? '' : ch}</span>
          <p class="eyebrow chapter-hero__eyebrow">${isPrologue ? 'Optional background' : `Chapter ${ch} of 18 · Part ${roman(part.id)}: ${part.title}`}</p>
          ${isPrologue ? '' : html`<p class="chapter-hero__sa deva" lang="sa">${meta.sa}</p>`}
          <h1 class="display chapter-hero__title">${meta.title}</h1>
          ${isPrologue ? '' : html`<p class="chapter-hero__name"><span class="iast" lang="sa-Latn">${meta.iast}</span><span aria-hidden="true"> · </span><span>${meta.nameMeaning}</span></p>`}
          <p class="chapter-hero__big">${meta.bigIdea}</p>
          <div class="chapter-hero__progress">
            <div class="bar chapter-hero__bar" role="progressbar" aria-label="Chapter progress" aria-valuemin="0" aria-valuemax="${s.total}" aria-valuenow="${s.done}"><div class="bar__fill" style="width:${(s.done / s.total) * 100}%"></div></div>
            <p class="small">${s.done} of ${plural(s.total, 'lesson')} complete${isPrologue ? '' : ` · ${s.versesRead} of ${meta.verses} verses read`}</p>
          </div>
          <a class="btn btn--primary chapter-hero__cta" href="${cta.href}">${cta.label} ${icon('arrow-right', { size: 20 })}</a>
        </div>
      </section>

      <div class="wrap chapter__body">
        ${isPrologue ? html`<section class="section">
          <p class="lede">${meta.summary}</p>
          <div class="callout chapter__optional">${icon('info', { size: 18 })}<p>These four lessons are optional and do not count towards your progress. Read them first if the story is new to you, or come back any time. <a class="text-link" href="/lesson/1-1">Start chapter 1</a></p></div>
        </section>` : html`
        <section class="section chapter__story" aria-labelledby="story-title">
          <h2 class="eyebrow" id="story-title">Where we are</h2>
          <p class="chapter__storytext">${meta.storySoFar}</p>
        </section>
        <section class="section" aria-labelledby="summary-title">
          <h2 class="h3" id="summary-title">What happens in this chapter</h2>
          <div class="prose muted chapter__summary">${paragraphs(meta.summary)}</div>
        </section>
        <section class="section" aria-labelledby="teach-title">
          <h2 class="h3" id="teach-title">Key teachings</h2>
          <ol class="teachings">${meta.keyTeachings.map((t, i) => html`<li><span class="teachings__n num" aria-hidden="true">${i + 1}</span><span>${t}</span></li>`)}</ol>
        </section>`}

        <section class="section" aria-labelledby="lessons-title">
          <div class="section__head">
            <h2 class="h3" id="lessons-title">${isPrologue ? 'Four short lessons' : 'Lessons'}</h2>
            ${isPrologue ? '' : html`<a class="link" href="/read/${ch}">${icon('read', { size: 16 })} Read whole chapter</a>`}
          </div>
          ${isPrologue ? '' : html`<p class="small muted chapter__lessons-note">The chapter's ${meta.verses} verses in ${meta.lessons.length} short lessons of about ten minutes, taken in order. Each covers a few verses, explains them, then asks a quick question or two.</p>`}
          <ol class="lesson-list">${meta.lessons.map((l) => lessonRow(l, progress, nextLesson && nextLesson.id))}</ol>
        </section>

        ${famous.length ? html`<section class="section" aria-labelledby="famous-title">
          <h2 class="h3" id="famous-title">Famous verses in this chapter</h2>
          <div class="stack">${famous.map((f) => html`<a class="card famous" href="/verse/${f.ch}/${f.v}">
            <p class="famous__tr">${f.translation}</p>
            <p class="famous__foot">${avatar(f.speaker)}<span>${SPEAKERS[f.speaker] ? SPEAKERS[f.speaker].name : ''} · ${f.id}</span>${icon('arrow-right', { size: 16 })}</p>
          </a>`)}</div>
        </section>` : ''}

        ${isPrologue ? '' : html`<nav class="pager section" aria-label="Chapters">
          ${prev ? html`<a class="pager__link" href="/chapter/${prev.ch}">${icon('arrow-left', { size: 18 })}<span><span class="pager__label">Chapter ${prev.ch}</span><span class="pager__title">${prev.title}</span></span></a>` : html`<a class="pager__link" href="/chapter/0">${icon('arrow-left', { size: 18 })}<span><span class="pager__label">Background</span><span class="pager__title">The story so far</span></span></a>`}
          ${next ? html`<a class="pager__link pager__link--next" href="/chapter/${next.ch}"><span><span class="pager__label">Chapter ${next.ch}</span><span class="pager__title">${next.title}</span></span>${icon('arrow-right', { size: 18 })}</a>` : ''}
        </nav>`}
      </div>
    </div>`,
  };
}
